import type { ForecastDayRecord, ForecastMetricRecord, ForecastSkillRecord } from '@navio/nsdk';
import { cva } from 'noddi-ui-common';
import { NavioSeriesChart, NoddiButton, NoddiCard, NoddiColumnDef, NoddiStaticTable } from 'noddi-ui-web';
import { CustomDateFormats, NoddiDateFormats, noddiFormatDate } from 'noddi-util';

import { ForecastStatusBadge } from './ForecastStatusBadge';
import { NavioForecastMetrics } from './NavioForecastMetrics';
import { skillColumns } from './forecastColumns';

interface Props {
  metrics: ForecastMetricRecord[];
  days: ForecastDayRecord[];
  skills: ForecastSkillRecord[];
  onSelectDay: (date: string) => void;
}

export const NavioForecastCapacity = ({ metrics, days, skills, onSelectDay }: Props) => (
  <div className='flex flex-col gap-6'>
    <NavioForecastMetrics metrics={metrics} />
    <NoddiCard
      title='Daily utilization'
      description='Select a day to explore bookable time, service demand and workers.'
    >
      <div className='grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-7'>
        {days.map((day) => (
          <NoddiButton
            key={day.date}
            variant='outline'
            className={dayVariants({ tone: day.tone })}
            onClick={() => onSelectDay(day.date)}
            aria-label={`Explore ${noddiFormatDate(day.date, CustomDateFormats.SHORT_DAY_MONTH_NUMERIC)}: ${day.status}, ${day.utilization === null ? 'utilization unavailable' : `${day.utilization}% utilization`}, ${day.scheduled === null ? 'scheduled cars unavailable' : `${day.scheduled} scheduled cars`}`}
          >
            <span>{noddiFormatDate(day.date, CustomDateFormats.SHORT_DAY_MONTH_NUMERIC)}</span>
            <span className='tabular-nums'>{day.utilization === null ? 'Unavailable' : `${day.utilization}%`}</span>
            <ForecastStatusBadge tone={day.tone} status={day.status} />
          </NoddiButton>
        ))}
      </div>
      {days.length === 0 && <p className='text-sm text-muted-foreground'>No daily utilization for this period.</p>}
    </NoddiCard>
    <NoddiCard title='Staffing demand'>
      {days.length === 0 ? (
        <p className='text-sm text-muted-foreground'>No staffing data for this period.</p>
      ) : (
        <NavioSeriesChart
          className='h-64 w-full'
          primaryAxisLabel='Workers'
          series={[
            { key: 'requiredWorkers', label: 'Required workers' },
            { key: 'availableWorkers', label: 'Available workers', dashed: true }
          ]}
          labelFormatter={(date) => noddiFormatDate(date, CustomDateFormats.SHORT_DAY_MONTH_NUMERIC)}
          tooltipLabelFormatter={(date) => noddiFormatDate(date, NoddiDateFormats.DATE)}
          data={days.map((day) => ({
            label: day.date,
            values: { requiredWorkers: day.requiredWorkers, availableWorkers: day.availableWorkers }
          }))}
        />
      )}
    </NoddiCard>
    <NoddiStaticTable
      title='Daily details'
      data={days}
      columns={dayColumns}
      getRowId={(day) => day.date}
      onRowClick={(day) => onSelectDay(day.date)}
      enableSearch={false}
      emptyStateText='No daily utilization for this period.'
    />
    <NoddiStaticTable
      title='Skill coverage'
      data={skills}
      columns={skillColumns}
      getRowId={(skill) => skill.key}
      enableSearch={false}
      emptyStateText='Skill coverage unavailable for this period.'
    />
  </div>
);

const dayColumns: NoddiColumnDef<ForecastDayRecord>[] = [
  { accessorKey: 'date', header: 'Date', meta: { type: 'date' } },
  {
    accessorKey: 'utilization',
    header: 'Utilization',
    cell: ({ row }) => (row.original.utilization === null ? null : `${row.original.utilization}%`)
  },
  { accessorKey: 'scheduled', header: 'Scheduled cars' },
  { accessorKey: 'remaining', header: 'Remaining car slots' },
  { accessorKey: 'requiredWorkers', header: 'Required workers' },
  { accessorKey: 'availableWorkers', header: 'Available workers' },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => <ForecastStatusBadge tone={row.original.tone} status={row.original.status} />
  }
];
const dayVariants = cva('flex h-auto flex-col gap-4 p-4', {
  variants: {
    tone: {
      neutral: 'bg-muted text-foreground',
      warning: 'border-warning bg-warning/10 text-foreground',
      destructive: 'border-destructive bg-destructive/10 text-foreground'
    }
  }
});
